import { useState } from "react";
import Top100Prospects from "./Top100Prospects.tsx";
import clsx from "clsx";
import { DataTable } from "../components/DataTable/index.tsx";
import type { Col, DataTableRow } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { getCols } from "../../common/getCols.ts";
import { POSITIONS } from "../../common/constants.ts";
import { helpers } from "../util/helpers.ts";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { wrappedCurrency } from "../components/wrappedCurrency.ts";
import { wrappedRatingWithChange } from "../components/RatingWithChange.tsx";
import { HelpPopover } from "../components/HelpPopover.tsx";
import type { View } from "../../common/types.ts";
import { showNotification } from "../util/showNotification.ts";
import { RatingsStatsPopover } from "../components/RatingsStatsPopover/index.tsx";
import { confirm } from "../util/confirm.tsx";

type Action = "promoteFromMinors" | "demoteToMinors";
type ViewName = "summary" | "ratings" | "positions" | "prospects";

const VIEWS: { key: ViewName; label: string }[] = [
	{ key: "summary", label: "Summary" },
	{ key: "ratings", label: "Ratings" },
	{ key: "positions", label: "Position Ratings" },
	{ key: "prospects", label: "Top 100 Prospects" },
];

// A prospect counts as covering a position when he is nearly as good there as at his best one.
// Without this the composition below shows a hole at, say, 2B while three shortstops who could
// play there sit in the system.
const COVER_MARGIN = 5;

const MinorLeagueComposition = ({
	players,
}: {
	players: View<"minorLeague">["players"];
}) => {
	const counts: Record<string, { primary: number; cover: number }> = {};
	for (const pos of POSITIONS) {
		counts[pos] = { primary: 0, cover: 0 };
	}

	for (const p of players) {
		const entry = counts[p.ratings.pos];
		if (entry) {
			entry.primary += 1;
		}

		const ovrs = p.ratings.ovrs;
		if (ovrs) {
			for (const pos of POSITIONS) {
				if (
					pos !== p.ratings.pos &&
					ovrs[pos] !== undefined &&
					ovrs[pos] >= p.ratings.ovr - COVER_MARGIN
				) {
					counts[pos]!.cover += 1;
				}
			}
		}
	}

	const columns = [
		["SP", "RP", "C"],
		["1B", "2B", "3B", "SS"],
		["LF", "CF", "RF", "DH"],
	];

	return (
		<div className="text-nowrap">
			<b>
				Farm System Composition{" "}
				<HelpPopover title="Farm System Composition">
					<p>
						How many prospects you have at each position. The second number is
						how many others could cover it, meaning they rate within{" "}
						{COVER_MARGIN} points of their own best position there.
					</p>
					<p>
						There are no recommended totals here. A farm system is not a roster,
						so being heavy at one position is a choice rather than a mistake. A
						position is only marked red when nobody plays it and nobody could
						cover it.
					</p>
				</HelpPopover>
			</b>
			<div className="mt-2 row">
				{columns.map((group, i) => (
					<div className="col-4" key={i}>
						{group.map((pos) => {
							const entry = counts[pos]!;
							const empty = entry.primary === 0 && entry.cover === 0;
							return (
								<div key={pos} className={empty ? "text-danger" : undefined}>
									{pos}: {entry.primary}
									{entry.cover > 0 ? ` (+${entry.cover})` : null}
								</div>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};

// Same DataTable as the rest of ZenGM: click a header to sort, click again to flip. First click puts the useful end
// on top (highest counts and rates, lowest ERA/WHIP). Each table keeps its own sort, also across organizations.
const statCol = (title: string, desc: string, lowerIsBetter = false): Col => ({
	title,
	desc,
	sortSequence: lowerIsBetter ? ["asc", "desc"] : ["desc", "asc"],
	sortType: "number",
});
const PLAYER_COLS: Col[] = [
	{ title: "Name", sortSequence: ["asc", "desc"], sortType: "name" },
	{ title: "Pos", sortSequence: ["asc", "desc"], sortType: "pos" },
	{ title: "Age", sortSequence: ["asc", "desc"], sortType: "number" },
];
const HITTING_COLS: Col[] = [
	...PLAYER_COLS,
	statCol("G", "Games"),
	statCol("PA", "Plate Appearances"),
	statCol("H", "Hits"),
	statCol("HR", "Home Runs"),
	statCol("RBI", "Runs Batted In"),
	statCol("AVG", "Batting Average"),
	statCol("OPS", "On-Base Plus Slugging"),
];
const PITCHING_COLS: Col[] = [
	...PLAYER_COLS,
	statCol("G", "Games Pitched"),
	statCol("IP", "Innings Pitched"),
	statCol("SO", "Strikeouts"),
	statCol("ERA", "Earned Run Average", true),
	statCol("WHIP", "Walks + Hits per Inning Pitched", true),
];
// Rates arrive formatted (".849", "45.2", "3.29"); sort on the number
const rateCell = (value: string) => ({ value, sortValue: Number.parseFloat(value) });
const nameCell = (
	p: { pid: number; name: string; calledUp: boolean },
	season: number,
) => ({
	value: (
		<>
			<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>{" "}
			<RatingsStatsPopover pid={p.pid} season={season} minorLeague />
			{p.calledUp ? (
				<span className="text-body-secondary"> (called up)</span>
			) : null}
		</>
	),
	sortValue: p.name,
	searchValue: p.name,
});

// Minimum minor league system: total and composition, shown for every organization. Only a warning for the user;
// AI organizations try to fix their own shortages on each regular season roster check.
const MinorsMinimumStatus = ({
	status: s,
	min,
	isUser,
}: {
	status: NonNullable<View<"minorLeague">["minorsStatus"]>;
	min: number;
	isUser: boolean;
}) => {
	const composition: string[] = [];
	if (s.short.fielders > 0) composition.push(`${s.short.fielders} position player${s.short.fielders === 1 ? "" : "s"}`);
	if (s.short.pitchers > 0) composition.push(`${s.short.pitchers} pitcher${s.short.pitchers === 1 ? "" : "s"}`);
	if (s.short.catchers > 0) composition.push(`${s.short.catchers} catcher${s.short.catchers === 1 ? "" : "s"} able to catch`);
	if (s.short.starters > 0) composition.push(`${s.short.starters} pitcher${s.short.starters === 1 ? "" : "s"} able to start`);
	if (s.uncovered.length > 0) composition.push(`coverage at ${s.uncovered.join(", ")}`);
	const short = s.short.total > 0 || composition.length > 0;
	return (
		<div className={clsx("mt-3", isUser && short ? "alert alert-warning" : "text-body-secondary")}>
			<div>
				Minor league minimum: {s.total} of {min} players ({s.fielders} position
				players of {s.targets.fielders}, {s.pitchers} pitchers of{" "}
				{s.targets.pitchers}; {s.catchers} able to catch, {s.starters} able to
				start).
			</div>
			{short ? (
				<>
					<div>
						Total: {s.short.total > 0 ? `${s.short.total} short` : "OK"}.
						Composition: {composition.length > 0 ? `short ${composition.join(", ")}` : "OK"}.
					</div>
					{isUser ? (
						<div>
							Nothing is done automatically for your organization and you can
							keep playing: add players by sending someone down, drafting,
							trading or signing free agents.
						</div>
					) : (
						<div>
							This AI organization tries to fill it with minimum-salary free
							agents on each regular season roster check, when one who helps is
							available.
						</div>
					)}
				</>
			) : null}
		</div>
	);
};

// Season Summary of the automatic minor league season. Read-only: nothing here is needed for the season to run.
const MinorLeagueSeasonSummary = ({
	summary,
	teamName,
}: {
	summary: NonNullable<View<"minorLeague">["seasonSummary"]>;
	teamName: string;
}) => {
	const playerLink = (p: { pid: number; name: string }) => (
		<>
			<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>{" "}
			<RatingsStatsPopover pid={p.pid} season={summary.season} minorLeague />
		</>
	);
	return (
		<>
			<h2 className="mt-4">{summary.season} Season Summary</h2>
			{!summary.hasData ? (
				<p className="text-body-secondary">
					No minor league games yet this season. Each regular season game
					between two big clubs is also a game between their affiliates.
				</p>
			) : (
				<>
					<p className="text-body-secondary">
						Affiliates play each other in the same matchups as their big
						clubs. Qualified for OPS: {summary.qualifiers.pa} PA. Qualified
						for ERA: {summary.qualifiers.ip} IP.
					</p>
					<div className="row">
						{summary.leaders.map((leader) => (
							<div className="col-sm-6 col-lg-4 mb-3" key={leader.title}>
								<h3 className="h6 mb-1">{leader.title}</h3>
								{leader.rows.length > 0 ? (
									<table className="table table-sm table-striped mb-0">
										<tbody>
											{leader.rows.map((p, i) => (
												<tr key={p.pid}>
													<td style={{ width: 20 }}>{i + 1}.</td>
													<td>
														{playerLink(p)}{" "}
														<span className="text-body-secondary">
															{p.pos}, {p.age}, {p.abbrev}
														</span>
													</td>
													<td className="text-end">{p.value}</td>
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<p className="text-body-secondary mb-0">None yet.</p>
								)}
							</div>
						))}
					</div>
					<h3 className="h5 mt-2">{teamName}: hitting</h3>
					{summary.hitters.length > 0 ? (
						<DataTable
							cols={HITTING_COLS}
							defaultSort={[4, "desc"]}
							hideAllControls
							name="MinorLeagueSeasonHitting"
							nonfluid
							small
							striped
							rows={summary.hitters.map((p) => ({
								key: p.pid,
								data: [
									nameCell(p, summary.season),
									{ value: p.pos, sortValue: p.pos },
									p.age,
									p.g,
									p.pa,
									p.h,
									p.hr,
									p.rbi,
									rateCell(p.avg),
									rateCell(p.ops),
								],
							}))}
						/>
					) : (
						<p className="text-body-secondary">No hitting stats.</p>
					)}
					<h3 className="h5 mt-2">{teamName}: pitching</h3>
					{summary.pitchers.length > 0 ? (
						<DataTable
							cols={PITCHING_COLS}
							defaultSort={[4, "desc"]}
							hideAllControls
							name="MinorLeagueSeasonPitching"
							nonfluid
							small
							striped
							rows={summary.pitchers.map((p) => ({
								key: p.pid,
								data: [
									nameCell(p, summary.season),
									{ value: p.pos, sortValue: p.pos },
									p.age,
									p.g,
									rateCell(p.ip),
									p.so,
									rateCell(p.era),
									rateCell(p.whip),
								],
							}))}
						/>
					) : (
						<p className="text-body-secondary">No pitching stats.</p>
					)}
				</>
			)}
		</>
	);
};

// Affiliates' records from the head-to-head minor league games. Compact, read-only; no playoffs or champion.
const MinorLeagueStandings = ({
	standings,
	tid,
}: {
	standings: NonNullable<View<"minorLeague">["minorStandings"]>;
	tid: number;
}) => {
	if (!standings.hasData) {
		return null;
	}
	const mine = standings.rows.find((r) => r.tid === tid);
	const ip = (outs: number) => `${Math.floor(outs / 3)}.${outs % 3}`;
	return (
		<>
			<h2 className="mt-4">{standings.season} Minor League Standings</h2>
			<p className="text-body-secondary">
				Each regular season game between two big clubs is also one game
				between their affiliates. Sorted by win%, then run differential, then
				wins.
				{" "}At the end of a complete season, the top 5 get +0.05 fan hype,
				the next 5 get +0.02, and the bottom 5 lose 0.03. The Top 100
				prospect ranking does not affect hype.
				{standings.startedMidSeason
					? " Records started with the season already under way, so they only cover games since then."
					: null}
			</p>
			<div className="table-responsive">
				<table
					className="table table-sm table-striped table-hover mb-1"
					style={{ maxWidth: 520 }}
				>
					<thead>
						<tr>
							<th>#</th>
							<th>Org</th>
							<th className="text-end">G</th>
							<th className="text-end">W</th>
							<th className="text-end">L</th>
							<th className="text-end">Pct</th>
							<th className="text-end">RS</th>
							<th className="text-end">RA</th>
							<th className="text-end" title="Fan hype adjustment applied at the end of the season">Hype</th>
						</tr>
					</thead>
					<tbody>
						{standings.rows.map((r, i) => (
							<tr
								key={r.tid}
								className={r.tid === tid ? "table-info" : undefined}
							>
								<td>{i + 1}</td>
								<td title={r.name}>
									<a href={helpers.leagueUrl(["minor_league", `${r.abbrev}_${r.tid}`])}>
										{r.abbrev}
									</a>
								</td>
								<td className="text-end">{r.g}</td>
								<td className="text-end">{r.w}</td>
								<td className="text-end">{r.l}</td>
								<td className="text-end">
									{r.g > 0 ? r.pct.toFixed(3).replace(/^0/, "") : "-"}
								</td>
								<td className="text-end">{r.rs}</td>
								<td className="text-end">{r.ra}</td>
								<td className="text-end">
									{r.hype === undefined || r.hype === 0 ? "" : `${r.hype > 0 ? "+" : ""}${r.hype.toFixed(2)}`}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{mine && (mine.rep.pa > 0 || mine.rep.outs > 0) ? (
				<p className="text-body-secondary small">
					{mine.abbrev} was short of healthy players in some games: unnamed
					replacement-level players took {mine.rep.pa} PA and pitched{" "}
					{ip(mine.rep.outs)} IP. Their numbers count in the score but not in
					any player's line.
				</p>
			) : null}
		</>
	);
};

// Past seasons: champion by record (best record of a complete season; no playoffs or final). Partial seasons keep
// their standings but have no title.
const MINOR_HISTORY_REASONS: Record<string, string> = {
	startedMidSeason: "partial: records started mid-season",
	missingGames: "partial: some organizations have no games",
	incomplete: "partial: not every game was recorded",
};
const MinorLeagueHistory = ({
	history,
}: {
	history: NonNullable<View<"minorLeague">["minorHistory"]>;
}) => {
	if (history.length === 0) {
		return null;
	}
	return (
		<>
			<h2 className="mt-4">Minor League History</h2>
			<p className="text-body-secondary">
				Champion by record: best regular season record (win%, then run
				differential, then wins). There are no minor league playoffs.
			</p>
			<div className="table-responsive">
				<table
					className="table table-sm table-striped mb-3"
					style={{ maxWidth: 520 }}
				>
					<thead>
						<tr>
							<th>Year</th>
							<th>Champion by record</th>
							<th className="text-end">W-L</th>
						</tr>
					</thead>
					<tbody>
						{history.map((h) => (
							<tr key={h.season}>
								<td>
									<a href={`${helpers.leagueUrl(["history", h.season])}#minors`}>
										{h.season}
									</a>
								</td>
								{h.complete && h.champion ? (
									<>
										<td title={`${h.champion.region} ${h.champion.name}`}>
											{h.champion.abbrev}
											{h.runnerUp ? (
												<span className="text-body-secondary">
													{" "}
													(2nd: {h.runnerUp.abbrev})
												</span>
											) : null}
										</td>
										<td className="text-end">
											{h.champion.w}-{h.champion.l}
										</td>
									</>
								) : (
									<td colSpan={2} className="text-body-secondary">
										No title ({MINOR_HISTORY_REASONS[h.reason ?? ""] ?? "partial"})
									</td>
								)}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
};

const MinorLeague = ({
	players,
	tid,
	userTid,
	teams,
	canManage,
	rosterPlayers,
	rosterSize,
	maxRosterSize,
	minRosterSize,
	minMinorsSize,
	maxMinorsSize,
	minorsStatus,
	ratings,
	stats,
	seasonSummary,
	minorStandings,
	minorHistory,
	topProspects,
	farmRanking,
	minorLeagueFreeAgencySeasons,
}: View<"minorLeague">) => {
	useTitleBar({ title: "Minor League" });

	const [view, setView] = useState<ViewName>("summary");

	const { challengeNoRatings, season } = useLocal([
		"challengeNoRatings",
		"season",
	]);

	const negotiate = async (pid: number) => {
		await toWorker("main", "createTrade", [
			{
				tid: userTid,
				pids: [],
				dpids: [],
				pidsExcluded: [],
				dpidsExcluded: [],
			},
			{ tid, pids: [pid], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
		]);
		window.location.href = helpers.leagueUrl(["trade"]);
	};
	const move = async (action: Action, pid: number) => {
		const result = await toWorker("main", action, { pids: [pid] });
		if (typeof result === "string") {
			showNotification({ type: "error", text: result });
		} else if (result && "info" in result) {
			// Informative only: the move already happened
			showNotification({ type: "info", text: result.info });
		}
	};

	const releaseMinorLeaguer = async (pid: number, name: string) => {
		const proceed = await confirm(
			`Release ${name}? A player on a minor league deal (up to 1.5x the minimum) costs nothing to release; anyone else is still owed his contract.`,
			{ okText: "Release player" },
		);
		if (!proceed) return;
		const errorMsg = await toWorker("main", "releasePlayer", { pids: [pid] });
		if (errorMsg) {
			showNotification({ type: "error", text: errorMsg });
		}
	};

	const positionColNames = POSITIONS.flatMap((pos) => [
		`rating:ovr${pos}`,
		`rating:pot${pos}`,
	]);

	const colNames =
		view === "ratings"
			? [
					"Name",
					"Pos",
					"Age",
					"Ovr",
					"Pot",
					...ratings.map((rating) => `rating:${rating}`),
					"Minors",
				]
			: view === "positions"
				? ["Name", "Pos", "Age", ...positionColNames, "Minors"]
				: [
						"Name",
						"Pos",
						"Age",
						"Ovr",
						"Pot",
						"Contract",
						"Exp",
						...stats.map((stat) => `stat:${stat}`),
						{
							title: "Big club",
							desc: "How he feels about waiting in the minors. Hover for the reasons.",
						},
						"Minors",
					];

	const cols = getCols(colNames, { Minors: { title: "" } });

	const buildRows = (
		list: typeof players,
		action: Action,
		label: string,
	): DataTableRow[] =>
		list.map((p) => {
			const name = wrappedPlayerNameLabels({
				injury: p.injury,
				jerseyNumber: p.jerseyNumber,
				pid: p.pid,
				skills: p.ratings.skills,
				defaultWatch: p.watch,
				firstName: p.firstName,
				firstNameShort: p.firstNameShort,
				lastName: p.lastName,
			});

			const button = canManage ? (
				<>
					<button
						className="btn btn-light-bordered btn-xs"
						onClick={() => move(action, p.pid)}
					>
						{label}
					</button>
					{action === "promoteFromMinors" ? (
						<button
							className="btn btn-light-bordered btn-xs ms-1"
							onClick={() =>
								releaseMinorLeaguer(p.pid, `${p.firstName} ${p.lastName}`)
							}
						>
							Release
						</button>
					) : null}
				</>
			) : (
				<button
					className="btn btn-light-bordered btn-xs"
					onClick={() => negotiate(p.pid)}
				>
					Negociar
				</button>
			);

			let middle: any[];
			if (view === "ratings") {
				middle = [
					challengeNoRatings
						? null
						: wrappedRatingWithChange(p.ratings.ovr, p.ratings.dovr),
					challengeNoRatings
						? null
						: wrappedRatingWithChange(p.ratings.pot, p.ratings.dpot),
					...ratings.map((rating) =>
						challengeNoRatings ? null : (p.ratings as any)[rating],
					),
				];
			} else if (view === "positions") {
				middle = POSITIONS.flatMap((pos) =>
					challengeNoRatings
						? [null, null]
						: [p.ratings.ovrs?.[pos], p.ratings.pots?.[pos]],
				);
			} else {
				middle = [
					challengeNoRatings
						? null
						: wrappedRatingWithChange(p.ratings.ovr, p.ratings.dovr),
					challengeNoRatings
						? null
						: wrappedRatingWithChange(p.ratings.pot, p.ratings.dpot),
					wrappedCurrency(p.contract.amount, "M"),
					{
						value: (
							<>
								{p.contract.exp}
								{p.minorLeagueFreeAgencySeason !== undefined ? (
									<div className="fw-bold text-danger text-nowrap" title="Agência livre das Minors nas renovações deste ano, mesmo com contrato até uma data posterior. Previsão baseada no elenco atual.">
										Saída Minor: {p.minorLeagueFreeAgencySeason}
									</div>
								) : null}
							</>
						),
						sortValue: p.contract.exp,
						searchValue: `${p.contract.exp}${p.minorLeagueFreeAgencySeason !== undefined ? ` Saída Minor: ${p.minorLeagueFreeAgencySeason}` : ""}`,
					},
					...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
					p.satisfaction
						? {
								value: (
									<span
										title={p.satisfaction.reasons.join("\n")}
										className={clsx({
											"text-success": p.satisfaction.label === "Ready",
										})}
									>
										{p.satisfaction.label}
									</span>
								),
								searchValue: p.satisfaction.label,
								sortValue: p.satisfaction.label,
							}
						: null,
				];
			}

			return {
				key: p.pid,
				classNames: { "table-warning": p.minorLeagueFreeAgencySeason !== undefined },
				metadata: {
					type: "player" as const,
					pid: p.pid,
					season,
					playoffs: "regularSeason" as const,
				},
				data: [name, p.ratings.pos, p.age, ...middle, button],
			};
		});

	// Sorting by overall is the useful default, except on Position Ratings where there is no
	// single overall column to sort by.
	const defaultSort: [number, "asc" | "desc"] =
		view === "positions" ? [2, "asc"] : [3, "desc"];

	return (
		<>
			<label className="form-label" htmlFor="minors-team">
				Team
			</label>
			<select
				id="minors-team"
				className="form-select mb-3"
				value={tid}
				onChange={(event) => {
					const t = teams.find((t) => t.tid === Number(event.target.value));
					if (t)
						window.location.href = helpers.leagueUrl([
							"minor_league",
							`${t.abbrev}_${t.tid}`,
						]);
				}}
			>
				{teams.map((t) => (
					<option key={t.tid} value={t.tid}>
						{t.name}
					</option>
				))}
			</select>
			{!canManage && (
				<p>
					Browse this team's prospects and pick them on the{" "}
					<a href={helpers.leagueUrl(["trade"])}>Trade</a> page. Minor leaguers
					stay in the minors with their new team.
				</p>
			)}
			<MinorLeagueComposition players={players} />
			{minorsStatus ? (
				<MinorsMinimumStatus
					status={minorsStatus}
					min={minMinorsSize}
					isUser={tid === userTid}
				/>
			) : null}

			<p className="mt-3">
				Players in the minors do not count against your {maxRosterSize} player
				roster limit or your payroll, and they do not play in major league
				games. Their affiliate plays a minor league game against the other
				organization's affiliate whenever the big club plays (see the
				standings and Season Summary below), and regular playing time helps a little
				with how they develop between seasons.
			</p>
			<p>
				O contrato fica congelado nas Minors: não é pago e não vence pelo prazo
				normal. Porém, jogadores com contrato de Minor (até 1,5 vez o mínimo),
				fora dos prospectos protegidos da organização, saem pela agência livre
				das Minors após {minorLeagueFreeAgencySeasons} temporadas profissionais.
				Para renovar ou renegociar, promova o jogador ao elenco principal antes
				das renovações.
			</p>
			<p>
				Active roster: {rosterSize} of {maxRosterSize} (minimum {minRosterSize}
				).
			</p>

			<ul className="nav nav-pills mb-3">
				{VIEWS.map(({ key, label }) => (
					<li className="nav-item" key={key}>
						<button
							className={clsx("nav-link", { active: view === key })}
							onClick={() => setView(key)}
						>
							{label}
						</button>
					</li>
				))}
			</ul>

			{view === "prospects" && topProspects ? (
				<Top100Prospects
					ranking={topProspects}
					farms={farmRanking ?? []}
					userTid={userTid}
				/>
			) : (
				<>
					<h2>
						In the minors ({players.length}
						{maxMinorsSize > 0 ? ` of ${maxMinorsSize} max` : ""})
					</h2>
					{players.some((p) => p.minorLeagueFreeAgencySeason !== undefined) ? (
						<p className="text-danger">
							<b>Saída prevista das Minors:</b> as linhas destacadas indicam quem
							vai sair nas próximas renovações pela regra de agência livre.
							O ano aparece na coluna Exp, separado do vencimento do contrato.
							A previsão acompanha o elenco atual e pode mudar com promoções,
							contratos ou alterações nos prospectos protegidos.
						</p>
					) : null}
					{players.length > 0 ? (
						<DataTable
							cols={cols}
							defaultSort={defaultSort}
							name={`MinorLeague:${view}`}
							rows={buildRows(players, "promoteFromMinors", "Call up")}
							pagination
						/>
					) : (
						<p className="text-body-secondary">
							This team has nobody in the minors.
						</p>
					)}

					<h2 className="mt-4">Active roster</h2>
					<DataTable
						cols={cols}
						defaultSort={defaultSort}
						name={`MinorLeagueRoster:${view}`}
						rows={buildRows(rosterPlayers, "demoteToMinors", "Send down")}
						pagination
					/>

				</>
			)}

			{minorStandings ? (
				<MinorLeagueStandings standings={minorStandings} tid={tid} />
			) : null}

			{minorHistory ? <MinorLeagueHistory history={minorHistory} /> : null}

			{seasonSummary ? (
				<MinorLeagueSeasonSummary
					summary={seasonSummary}
					teamName={teams.find((t) => t.tid === tid)?.name ?? ""}
				/>
			) : null}
		</>
	);
};

export default MinorLeague;

